import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/DeviationThumbNodeView';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  getExternalRenderer,
  getNodeFromSelection,
} from '../../components/utils/tiptap';
import { CSSProperties } from 'react';
import { EXTENSION_TYPE_NAME } from './constants';
import { NodeSelection } from '@tiptap/pm/state';
import { Url } from '@wix/da-url';

export interface DaDeviationThumbOptions {
  defaultWidth: number;
  maxWidth?: number;
  maxHeight?: number;
  canLink?: boolean;
  canConfigure?: boolean;
}

export interface DaDeviationThumbAttrs {
  link: string | null;
  width: number | null;
  height: number | null;
  cropping: CSSProperties['objectFit'];
  deviation: PapiDeviation;
  aspect: number | null;
}

export enum MenuState {
  HIDDEN,
  SHOW,
}

export type DaDeviationThumbStorage = {
  menuState: MenuState;
  deviationNode?: any;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    deviationThumb: {
      daDeviationThumbAddDeviation: (deviation: PapiDeviation) => ReturnType;
      daDeviationThumbUpdateAttrs: (
        attrs: Partial<DaDeviationThumbAttrs>
      ) => ReturnType;
      daDeviationThumbOpenSettingsMenu: () => ReturnType;
      daDeviationThumbCloseSettingsMenu: () => ReturnType;
      daDeviationThumbLink: (href: string) => ReturnType;
      daDeviationThumbReplace: (deviation: PapiDeviation) => ReturnType;
      daDeviationThumbRefresh: (deviation: PapiDeviation) => ReturnType;
      daDeviationThumbDelete: () => ReturnType;
      daDeviationThumbConvertToFullview: () => ReturnType;
      daDeviationThumbToLink: () => ReturnType;
    };
  }
}

/**
 * Note that this wraps in <code> tag so that copy/paste works as span
 * seems to be ignored by tiptap. Not great but it works.
 */
export const DaDeviationThumb = Node.create<
  DaDeviationThumbOptions,
  DaDeviationThumbStorage
>({
  priority: 1,
  name: 'da-deviation-thumb',
  group: 'inline unlinkable',
  inline: true,
  draggable: true,

  addStorage() {
    return {
      menuState: MenuState.HIDDEN,
    };
  },

  addOptions(): DaDeviationThumbOptions {
    return {
      defaultWidth: 150,
      canLink: true,
      canConfigure: true,
    };
  },

  addAttributes() {
    return {
      deviation: {
        default: null,
      },
      link: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      cropping: {
        default: 'fill',
      },
      aspect: {
        default: null,
      },
    };
  },

  /**
   * Used to parse incoming html, usually via paste
   */
  parseHTML() {
    return [
      {
        tag: `div[data-da-type="da-deviation-thumb"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }

          const dataType = node.getAttribute('data-da-type');
          if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
            return false;
          }
          try {
            return {
              deviation: JSON.parse(node.getAttribute('data-deviation') ?? ''),
              link: node.getAttribute('data-link') ?? undefined,
              width: node.getAttribute('data-width') ?? undefined,
              height: node.getAttribute('data-height') ?? undefined,
              cropping: node.getAttribute('data-cropping') ?? undefined,
              aspect: node.getAttribute('data-aspect') ?? undefined,
            };
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * This seems to be called for some reason on unmount and then everything is also
   * undefined so make sure you guard things.
   */
  renderHTML({
    HTMLAttributes: { deviation, width, height, link, cropping, aspect },
  }) {
    try {
      const deviationMediaService = getExternalRenderer(
        this.editor,
        'mediaService'
      );

      const passAttribs = {
        'data-da-type': EXTENSION_TYPE_NAME,
        'data-deviation': JSON.stringify(deviation),
        'data-width': width,
        'data-height': height,
        'data-link': link,
        'data-cropping': cropping,
        'data-aspect': aspect,
      };

      const thumb = deviationMediaService.getSmallestThumbThatFullyCovers(
        deviation,
        width ?? 150,
        height ?? Infinity,
        t => t[t.length - 1], // Take largest if nothing covers,
        {
          thumbTypes: ['375W', '414W', '750W', '828W'],
        }
      );

      let thumbSpec: any[] = [];
      // Its probably text
      if (!thumb) {
        thumbSpec = [
          'a',
          mergeAttributes({ ...passAttribs, href: link }),
          deviation.title,
        ];
      } else {
        const img = [
          'img',
          mergeAttributes({
            alt: deviation.title,
            src: deviationMediaService.getLargestFullview(deviation).src,
            width,
            height,
          }),
        ];
        thumbSpec = [
          'a',
          mergeAttributes({ href: link ?? deviation.url }),
          img,
        ];
      }

      return [
        'code',
        mergeAttributes({
          ...passAttribs,
        }),
        thumbSpec,
      ];
    } catch (e) {
      return ['span'];
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component as any, {
      contentDOMElementTag: 'span',
    });
  },

  onSelectionUpdate() {
    if (this.editor.isActive(EXTENSION_TYPE_NAME)) {
      this.editor.commands.daDeviationThumbOpenSettingsMenu();
    } else {
      this.editor.commands.daDeviationThumbCloseSettingsMenu();
    }
  },

  addCommands() {
    return {
      daDeviationThumbAddDeviation:
        deviation =>
        ({ commands, dispatch }) => {
          if (dispatch) {
            return commands.insertContent({
              type: this.name,
              attrs: {
                deviation: toLightweightDeviation(deviation),
              },
            });
          }
          return true;
        },
      daDeviationThumbOpenSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
          }
          return true;
        },
      daDeviationThumbCloseSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.HIDDEN;
          }
          return true;
        },
      daDeviationThumbLink:
        href =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canLink) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              link: href,
            });
          }
          return true;
        },
      daDeviationThumbUpdateAttrs:
        attrs =>
        ({ editor, chain, dispatch, commands, state, tr }) => {
          if (dispatch) {
            // Find this node and store is position
            const nodes = getNodeFromSelection(editor);
            let nodePos;
            if (nodes[1]) {
              state.doc.descendants((node, pos) => {
                if (node === nodes[1]) {
                  nodePos = pos;
                  return false; // Stop traversing once we find the first paragraph
                }
                return true;
              });
            }
            if (nodePos) {
              // Update the attrs
              commands.updateAttributes(this.name, {
                ...attrs,
              });

              // Restore the selection because for some reason inlines
              // seem to select next after updates...
              const selection = NodeSelection.create(tr.doc, nodePos);
              tr.setSelection(selection);
              dispatch(tr);

              return true;
            }
          }
          return true;
        },
      daDeviationThumbDelete:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const node = (selection as any).node;

            if (node) {
              const { tr } = state;
              tr.deleteRange(selection.from, selection.to);
              dispatch(tr);
            }
            return true;
          }
          return true;
        },
      daDeviationThumbRefresh:
        deviation =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            commands.updateAttributes(this.name, {
              deviation: toLightweightDeviation(deviation),
            });
            return true;
          }
          return true;
        },
      daDeviationThumbReplace:
        deviation =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            commands.updateAttributes(this.name, {
              deviation: toLightweightDeviation(deviation),
            });
            return true;
          }
          return true;
        },
      daDeviationThumbConvertToFullview:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const { from, to } = selection;
            let attributes: DaDeviationThumbAttrs = {} as any;

            state.doc.nodesBetween(from, to, node => {
              if (node.type.name === this.name) {
                attributes = node.attrs as DaDeviationThumbAttrs;
              }
            });

            if (attributes !== undefined) {
              return chain()
                .daDeviationThumbDelete()
                .daDeviationAddDeviation(attributes.deviation)
                .daDeviationLink(attributes.link ?? '')
                .run();
            }
            return true;
          }
          return true;
        },
      daDeviationThumbToLink:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const { from, to } = selection;
            let attributes: any = null;

            state.doc.nodesBetween(from, to, node => {
              if (node.type.name === this.name) {
                attributes = node.attrs;
              }
            });

            if (attributes && attributes.deviation) {
              const deviation: PapiDeviation = attributes.deviation;
              let link = '';
              if (deviation.isPublished) {
                link = deviation.url ?? deviation.shortUrl ?? '';
              } else {
                link = Url.stashItemLink(
                  deviation.stashPrivateid?.toString(36) ?? ''
                );
              }
              chain()
                .daDeviationDelete()
                .insertContent({
                  type: 'text',
                  marks: [
                    {
                      type: 'link',
                      attrs: {
                        href: link,
                      },
                    },
                  ],
                  text: deviation.title ?? link,
                })
                .run();
            }
            return true;
          }
          return true;
        },
    };
  },
});
